// @flow

import get from 'lodash/get';
import Service from './service';
import { Tracking } from './tracking';
import { Braze } from './braze-service';
import type { AddressType } from '../types/address';
import type { MemberType } from '../types/member';
import type { IfpAppType } from '../types/ifp-app';
import type { UserType } from '../types/user';
import type { HttpResponseType } from '../types/http-response';

type LoginArgs = {
  email: string,
  password: string,
  source: string,
}

type ChangeEmailArgs = {
  newEmail: string,
  oldEmail: string,
  password: string,
}

type CreateWithResourcesArgs = {
  address: AddressType,
  apps: Array<IfpAppType>,
  email: string,
  members: Array<MemberType>,
  partnerName: string,
  password: string,
  source: string,
};

function pickUser(response: HttpResponseType): UserType {
  return get(response, 'data.data.user', response.data);
}

export default class UserService extends Service {
  token: string;
  tokenExp: Date;

  get(): Promise<UserType> {
    return this.api.get(`${this.baseUrl}/user`)
      .then(pickUser);
  }

  post(data: UserType): Promise<UserType> {
    return this.api.post(`${this.baseUrl}/user`, { user: data })
      .then(pickUser);
  }

  requestReset(body: {
    email: string,
    redirect?: string,
    source?: string,
    newExp?: bool // remove when angular login is taken down
  }): Promise<Object> {
    return this.api.post(`${this.baseUrl}/password/reset`, body);
  }

  resetPassword(code: string, body: { email: string, password: string }): Promise<Object> {
    return this.api.post(`${this.baseUrl}/password/reset/${code}`, body);
  }

  login({ email, password, source }: LoginArgs): Promise<UserType> {
    return this.api.post(`${this.baseUrl}/user/login`, { email, password })
      .then(pickUser)
      .then((user: UserType) => this.alias(user))
      .then(({ user }) => {
        Tracking.track('Log In', { Source: source });
        Braze.changeUser(user);
        return user;
      });
  }

  logout(): Promise<boolean> {
    return this.api.get(`${this.baseUrl}/user/logout`)
      .then(() => {
        Tracking.track('Log Out');
        Tracking.clearCookie();
        return true;
      })
      .catch((err: HttpResponseType) => {
        this.logger.requestError(err);
        return false;
      });
  }

  alias(user: UserType): Promise<{ user: UserType, didAlias: boolean }> {
    // Check if the user is already aliased
    if (user.isAliased) {
      Tracking.identify(user);
      return Promise.resolve({ user, didAlias: false });
    }

    return this.api.post(`${this.baseUrl}/user/alias`, { is_aliased: true })
      .then(() => {
        // If not aliased, do a mixpanel.alias
        Tracking.alias(user);
        return { user, didAlias: true };
      })
      .catch(() => ({ user, didAlias: false }));
  }

  createWithResources(args: CreateWithResourcesArgs):
  Promise<{ user: UserType, apps: Array<IfpAppType> }> {
    const source = args.source || 'account';
    const partnerName = args.partnerName || null;
    return this.api.post(`${this.baseUrl}/user/resources`, {
      ...args,
      source,
    })
      .then(res => Promise.all([
        res.data,
        this.alias(res.data.user),
      ]))
      .then((responses) => {
        const { user } = responses[1];
        if (partnerName) {
          const setOnce = true;
          Tracking.setPeopleProps({ initial_partner: partnerName }, setOnce);
        }
        Tracking.track('Sign Up', { Source: source });
        Tracking.trackGtmEvent('Sign Up', { Source: source });
        Braze.changeUser(user);
        return responses[0];
      });
  }

  loginWithResources(args: CreateWithResourcesArgs):
  Promise<{ user: UserType, apps: Array<IfpAppType> }> {
    const source = args.source || 'account';
    const partnerName = args.partnerName || null;
    return this.api.post(`${this.baseUrl}/user/resources-login`, {
      ...args,
      source,
    })
      .then(({ data }) => {
        const { data: { apps, user } } = data;
        return Promise.all([
          apps,
          // $FlowFixMe
          this.alias(user),
        ]);
      })
      .then((responses) => {
        const { user } = responses[1];
        if (partnerName) {
          Tracking.setPeopleProps({ current_partner: partnerName });
        }
        Tracking.track('Log In', { Source: source });
        Tracking.trackGtmEvent('Log In', { Source: source });
        Braze.changeUser(user);
        return user;
      });
  }

  changeEmail({ newEmail, oldEmail, password }: ChangeEmailArgs): Promise<UserType> {
    if (newEmail === oldEmail) { return Promise.reject(new Error('Emails are matching')); }
    return this.api.post(`${this.baseUrl}/user/change-email`, {
      newEmail,
      oldEmail,
      password,
    })
      .then((res) => {
        Tracking.track('Email Changed');
        return res.data;
      });
  }
}
